"use client";

import { createContext, useContext, useEffect, useState, ReactNode } from "react";
import { apiGet, apiPost, type Rol } from "@/lib/api";

export type User = {
  id: string;
  nombre: string;
  email: string;
  roles: Rol[];
  debeCambiarPassword: boolean;
};

// /auth/me y /auth/login devuelven {id, email, nombre, roles, debe_cambiar_password, ...}.
function toUser(d: unknown): User | null {
  const o = d as {
    id?: string;
    email?: string;
    nombre?: string;
    roles?: Rol[];
    debe_cambiar_password?: boolean;
  };
  if (!o || !o.email || !o.id) return null;
  return {
    id: o.id,
    nombre: o.nombre || o.email,
    email: o.email,
    roles: o.roles ?? [],
    debeCambiarPassword: o.debe_cambiar_password ?? false,
  };
}

type AuthState = {
  user: User | null;
  loading: boolean;
  /** Puede crear/editar producto, versiones, ECN… (admin o ingeniería). */
  puedeEscribir: boolean;
  esAdmin: boolean;
  login: (email: string, password: string) => Promise<void>;
  changePassword: (actual: string, nueva: string) => Promise<void>;
  logout: () => Promise<void>;
};

const AuthContext = createContext<AuthState>({
  user: null,
  loading: true,
  puedeEscribir: false,
  esAdmin: false,
  login: async () => {},
  changePassword: async () => {},
  logout: async () => {},
});

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // La cookie httpOnly (si existe) va sola; preguntamos quién es.
    apiGet("/auth/me")
      .then((d) => setUser(toUser(d)))
      .catch(() => setUser(null))
      .finally(() => setLoading(false));
  }, []);

  const login = async (email: string, password: string) => {
    const res = await apiPost<{ user: unknown }>("/auth/login", { email, password });
    setUser(toUser(res.user));
  };

  const changePassword = async (actual: string, nueva: string) => {
    // El backend reemite la cookie; refrescamos el usuario para recoger debe_cambiar_password=false.
    await apiPost("/auth/change-password", { password_actual: actual, password_nueva: nueva });
    await apiGet("/auth/me")
      .then((d) => setUser(toUser(d)))
      .catch(() => {});
  };

  const logout = async () => {
    await apiPost("/auth/logout", {}).catch(() => {});
    setUser(null);
  };

  const roles = user?.roles ?? [];
  const esAdmin = roles.includes("admin");
  const puedeEscribir = esAdmin || roles.includes("ingenieria");

  return (
    <AuthContext.Provider
      value={{ user, loading, puedeEscribir, esAdmin, login, changePassword, logout }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
